/**
 * Icon library for AI-generated whiteboard diagrams.
 *
 * Gemini generates text, not images, so the model selects an icon by NAME from
 * this catalogue and we resolve it to an inline SVG data URI that Excalidraw
 * embeds as an image element. That keeps icons free, instant, offline-capable
 * and visually consistent — every icon is a 24x24 outline glyph on the same
 * 1.6px stroke grid, matching the design system's Lucide-style icon rule.
 *
 * Adding an icon: add a `d` path (or raw body) here and it becomes immediately
 * available to the model — ICON_NAMES is derived, so the prompt stays in sync.
 */

/** Raw 24x24 viewBox path data, stroke-based. */
const ICON_PATHS: Record<string, string> = {
  // ── Infrastructure ────────────────────────────────────────────
  server: '<rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><path d="M6 6h.01M6 18h.01"/>',
  database: '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5"/><path d="M3 12c0 1.7 4 3 9 3s9-1.3 9-3"/>',
  cloud: '<path d="M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.7 1.5A3.5 3.5 0 0 0 6.5 19z"/>',
  storage: '<rect x="3" y="4" width="18" height="5" rx="1"/><rect x="3" y="10" width="18" height="5" rx="1"/><rect x="3" y="16" width="18" height="4" rx="1"/>',
  network: '<circle cx="12" cy="5" r="2.5"/><circle cx="5" cy="19" r="2.5"/><circle cx="19" cy="19" r="2.5"/><path d="M12 7.5v4M12 11.5 6.5 17M12 11.5 17.5 17"/>',
  router: '<rect x="2" y="13" width="20" height="8" rx="2"/><path d="M6 17h.01M10 17h.01"/><path d="M12 9V3M12 3l-3 3M12 3l3 3"/>',
  container: '<path d="M12 2 3 7v10l9 5 9-5V7z"/><path d="M3 7l9 5 9-5M12 12v10"/>',
  cpu: '<rect x="6" y="6" width="12" height="12" rx="1"/><path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4"/>',

  // ── Application ───────────────────────────────────────────────
  api: '<path d="M4 7h4a3 3 0 0 1 0 6H4zM4 7v10"/><path d="M13 17V7h3a3 3 0 0 1 0 6h-3"/><path d="M21 7v10"/>',
  service: '<circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1"/>',
  queue: '<rect x="2" y="8" width="5" height="8" rx="1"/><rect x="9.5" y="8" width="5" height="8" rx="1"/><rect x="17" y="8" width="5" height="8" rx="1"/>',
  webhook: '<path d="M9 12a3 3 0 1 1 4.2 2.75L16 20"/><path d="M15.5 11.5a3 3 0 1 1-2.3 5.2H7"/><path d="M8.5 17a3 3 0 1 1 .3-5.9L11.5 6"/>',
  code: '<path d="m8 6-6 6 6 6M16 6l6 6-6 6"/>',
  terminal: '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m6 9 3 3-3 3M12 15h5"/>',
  browser: '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 9h20M6 6.5h.01M9 6.5h.01"/>',
  mobile: '<rect x="6" y="2" width="12" height="20" rx="2"/><path d="M11 18.5h2"/>',
  desktop: '<rect x="2" y="4" width="20" height="12" rx="2"/><path d="M8 20h8M12 16v4"/>',
  app: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',

  // ── Data & analytics ──────────────────────────────────────────
  chart: '<path d="M3 3v18h18"/><path d="m7 14 3-4 3 3 5-7"/>',
  analytics: '<path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="4" width="3" height="14"/>',
  report: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h5"/>',
  filter: '<path d="M3 4h18l-7 8v6l-4 2v-8z"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>',

  // ── People & orgs ─────────────────────────────────────────────
  user: '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6"/>',
  users: '<circle cx="9" cy="8" r="3.5"/><path d="M2 21c0-3.5 3.1-5.5 7-5.5s7 2 7 5.5"/><path d="M17 8.2a3.5 3.5 0 0 1 0 6.6M18 21c0-2.4-.7-4-2-5"/>',
  team: '<circle cx="12" cy="6" r="3"/><circle cx="5" cy="17" r="3"/><circle cx="19" cy="17" r="3"/><path d="M12 9v3M9.5 14.5 12 12l2.5 2.5"/>',
  customer: '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6"/><path d="m18 3 1 2 2 .3-1.5 1.4.4 2L18 7.8 16.1 8.7l.4-2L15 5.3 17 5z"/>',
  admin: '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6"/><path d="M17 2.5 20.5 4v3c0 1.8-1.5 3-3.5 3.6-2-.6-3.5-1.8-3.5-3.6V4z"/>',

  // ── Flow & process ────────────────────────────────────────────
  process: '<rect x="3" y="6" width="18" height="12" rx="2"/><path d="M8 12h8"/>',
  decision: '<path d="M12 2 22 12 12 22 2 12z"/>',
  start: '<circle cx="12" cy="12" r="9"/><path d="m10 8 6 4-6 4z"/>',
  stop: '<circle cx="12" cy="12" r="9"/><rect x="9" y="9" width="6" height="6" rx="1"/>',
  check: '<circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/>',
  warning: '<path d="M12 3 2 20h20z"/><path d="M12 9v5M12 17h.01"/>',
  error: '<circle cx="12" cy="12" r="9"/><path d="m9 9 6 6M15 9l-6 6"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  refresh: '<path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 3v6h-6"/>',
  sync: '<path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/>',

  // ── Documents & content ───────────────────────────────────────
  document: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h8"/>',
  folder: '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>',
  file: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/>',
  image: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.8"/><path d="m4 18 5-5 4 4 3-3 4 4"/>',
  mail: '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m2 7 10 6 10-6"/>',
  message: '<path d="M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8z"/>',
  notification: '<path d="M18 9a6 6 0 1 0-12 0c0 6-3 7-3 7h18s-3-1-3-7"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/>',

  // ── Security ──────────────────────────────────────────────────
  lock: '<rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/>',
  unlock: '<rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 7.5-2"/>',
  shield: '<path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5z"/>',
  key: '<circle cx="8" cy="15" r="4"/><path d="m11 12 8-8 2 2-2 2 2 2-2 2-2-2-2 2z"/>',

  // ── Commerce & misc ───────────────────────────────────────────
  cart: '<circle cx="9" cy="20" r="1.5"/><circle cx="18" cy="20" r="1.5"/><path d="M2 3h3l2.6 12h11L21 7H6"/>',
  payment: '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h3"/>',
  package: '<path d="M12 2 3 7v10l9 5 9-5V7z"/><path d="M3 7l9 5 9-5M12 12v10"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2 2 2 0 1 1-4 0 1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3 15a2 2 0 1 1 0-4 1.7 1.7 0 0 0 1.2-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9 4.6a2 2 0 1 1 4 0 1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1A1.7 1.7 0 0 0 21 11a2 2 0 1 1 0 4z"/>',
  link: '<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/>',
  star: '<path d="m12 2 3 6.6 7 .9-5 4.8 1.3 7L12 18l-6.3 3.3L7 14.3 2 9.5l7-.9z"/>',
  flag: '<path d="M4 22V4M4 4h13l-2.5 4L17 12H4"/>',
  globe: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18"/>',
  ai: '<path d="M12 3v3M12 18v3M3 12h3M18 12h3"/><rect x="6" y="6" width="12" height="12" rx="3"/><circle cx="10" cy="11" r="1"/><circle cx="14" cy="11" r="1"/><path d="M10 15h4"/>',
  bot: '<rect x="4" y="8" width="16" height="12" rx="3"/><path d="M12 8V4M9 4h6"/><circle cx="9" cy="14" r="1.2"/><circle cx="15" cy="14" r="1.2"/>',
};

/** Every icon name the model may choose from. Derived, so it never drifts. */
export const ICON_NAMES = Object.keys(ICON_PATHS).sort();

/**
 * Resolves an icon name to an SVG data URI Excalidraw can embed.
 *
 * Returns null for an unknown name so callers can skip the element rather than
 * render a broken image — the model does occasionally invent icon names.
 */
export function getIconDataUri(name: string, color = '#1e1e1e'): string | null {
  const body = ICON_PATHS[name?.toLowerCase?.().trim()];
  if (!body) return null;

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ` +
    `stroke="${color}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">` +
    `${body}</svg>`;

  // encodeURIComponent (not base64) keeps the URI readable in saved scenes and
  // avoids btoa() throwing on any non-Latin1 character.
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** True when the name resolves to a real icon. */
export function isKnownIcon(name: string): boolean {
  return Boolean(ICON_PATHS[name?.toLowerCase?.().trim()]);
}

/**
 * Best-effort match for a model-supplied name that isn't an exact key —
 * e.g. "databases" → "database", "user-icon" → "user".
 */
export function resolveIconName(name: string): string | null {
  if (!name) return null;
  const raw = name.toLowerCase().trim().replace(/[\s_-]*icon$/, '').replace(/[\s_-]+/g, '');
  if (ICON_PATHS[raw]) return raw;

  const singular = raw.replace(/s$/, '');
  if (ICON_PATHS[singular]) return singular;

  // Substring match either direction, longest key first so "database" beats "data".
  const keys = Object.keys(ICON_PATHS).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (raw.includes(key) || key.includes(raw)) return key;
  }
  return null;
}
